import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Loader2,
  FolderOpen,
  Upload,
  FileText,
  Image as ImageIcon,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  Download,
  Search,
  Filter,
  X,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// File type icons and colors
const getFileIcon = (fileType) => {
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType)) {
    return { icon: ImageIcon, color: 'text-blue-500', bg: 'bg-blue-50' };
  }
  return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Format date
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('ca-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const AdminDocuments = () => {
  const { isAdmin, authFetch } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [copiedId, setCopiedId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadCategory, setUploadCategory] = useState('general');

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API_URL}/api/documents`, {  });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Error carregant documents');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/documents/categories`, {  });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      if (!uploadTitle) {
        setUploadTitle(file.name.replace(/\.[^/.]+$/, ''));
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Selecciona un fitxer');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle || uploadFile.name);
      formData.append('description', uploadDescription);
      formData.append('category', uploadCategory);

      const response = await authFetch(`${API_URL}/api/admin/documents/upload`, {
        method: 'POST',
        
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Document pujat correctament');
        
        // Reset form
        setUploadFile(null);
        setUploadTitle('');
        setUploadDescription('');
        setUploadCategory('general');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Refresh list
        fetchDocuments();
        fetchCategories();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error pujant document');
      }
    } catch (error) {
      toast.error('Error pujant document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Segur que vols eliminar aquest document?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/documents/${docId}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        toast.success('Document eliminat');
        fetchDocuments();
      } else {
        toast.error('Error eliminant document');
      }
    } catch (error) {
      toast.error('Error eliminant document');
    }
  };

  const getDocumentUrl = (doc) => {
    return `${API_URL}/api/documents/file/${doc.id}`;
  };

  const copyToClipboard = async (doc) => {
    const url = getDocumentUrl(doc);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(doc.id);
      toast.success('URL copiada al porta-retalls');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Error copiant URL');
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.original_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || 
                       (filterType === 'pdf' && doc.file_type === 'pdf') ||
                       (filterType === 'image' && ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(doc.file_type));
    const matchesCategory = filterCategory === 'all' || doc.category === filterCategory;
    
    return matchesSearch && matchesType && matchesCategory;
  });

  const isImage = (fileType) => ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(fileType);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-documents">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <FolderOpen className="w-6 h-6" />
              Repositori de Documents
            </h1>
            <p className="text-[#666666] mt-1">
              Puja i gestiona PDFs i imatges per enllaçar al contingut
            </p>
          </div>
          <div className="text-sm text-[#888888]">
            {documents.length} documents
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white border border-[#E5E5E5] rounded-lg p-6">
          <h3 className="font-semibold text-[#333333] flex items-center gap-2 mb-4">
            <Upload className="w-5 h-5" />
            Pujar Document
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Fitxer (PDF, PNG, JPG, etc.)</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.svg"
                onChange={handleFileSelect}
                className="cursor-pointer"
                data-testid="file-input"
              />
              {uploadFile && (
                <p className="text-xs text-[#888888]">
                  {uploadFile.name} ({formatFileSize(uploadFile.size)})
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Títol</Label>
              <Input
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                placeholder="Taula Slope..."
                data-testid="title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Categoria</Label>
              <select
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="w-full h-10 px-3 border border-input rounded-md text-sm"
              >
                <option value="general">General</option>
                <option value="taules">Taules i Estadístiques</option>
                <option value="normativa">Normativa</option>
                <option value="formularis">Formularis</option>
                <option value="galeria">Galeria</option>
                <option value="altres">Altres</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="w-full bg-[#1A4D2E] hover:bg-[#153d25] text-white gap-2"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Pujar
              </Button>
            </div>
          </div>
          
          <div className="mt-3">
            <Label>Descripció (opcional)</Label>
            <Input
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="Descripció del document..."
              className="mt-1"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px] max-w-md relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cercar documents..."
              className="pl-9"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#888888]" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="h-10 px-3 border border-input rounded-md text-sm"
            >
              <option value="all">Tots els tipus</option>
              <option value="pdf">PDFs</option>
              <option value="image">Imatges</option>
            </select>
            
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="h-10 px-3 border border-input rounded-md text-sm"
            >
              <option value="all">Totes les categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Documents List */}
        {filteredDocuments.length === 0 ? (
          <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-12 text-center">
            <FolderOpen className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
            <p className="text-[#666666]">
              {searchTerm || filterType !== 'all' || filterCategory !== 'all' 
                ? 'No s\'han trobat documents amb aquests filtres'
                : 'No hi ha documents. Puja el primer!'}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F9F9F7]">
                <tr>
                  <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3">Document</th>
                  <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3 hidden md:table-cell">Categoria</th>
                  <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3 hidden lg:table-cell">Mida</th>
                  <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3 hidden lg:table-cell">Data</th>
                  <th className="text-center text-xs font-semibold text-[#666666] uppercase px-4 py-3">Accions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E5E5]">
                {filteredDocuments.map((doc) => {
                  const { icon: FileIcon, color, bg } = getFileIcon(doc.file_type);
                  const docUrl = getDocumentUrl(doc);
                  
                  return (
                    <tr key={doc.id} className="hover:bg-[#F9F9F7]" data-testid={`doc-row-${doc.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center`}>
                            <FileIcon className={`w-5 h-5 ${color}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-[#333333] truncate">{doc.title}</p>
                            <p className="text-xs text-[#888888] truncate">{doc.original_name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="px-2 py-1 text-xs font-medium text-[#666666] bg-[#F5F5F5] rounded">
                          {doc.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-[#666666]">
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell text-sm text-[#666666]">
                        {formatDate(doc.uploaded_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          {/* Preview for images */}
                          {isImage(doc.file_type) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPreviewDoc(doc)}
                              title="Vista prèvia"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          
                          {/* Copy URL */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(doc)}
                            title="Copiar URL"
                            className={copiedId === doc.id ? 'text-green-500' : ''}
                          >
                            {copiedId === doc.id ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          
                          {/* Open in new tab */}
                          <a
                            href={docUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent"
                            title="Obrir en nova pestanya"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          
                          {/* Delete - Admin only */}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(doc.id)}
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Preview Modal */}
        {previewDoc && (
          <div 
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setPreviewDoc(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh] overflow-auto bg-white rounded-lg">
              <button
                onClick={() => setPreviewDoc(null)}
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={getDocumentUrl(previewDoc)}
                alt={previewDoc.title}
                className="max-w-full max-h-[85vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="p-4 bg-white border-t">
                <p className="font-medium">{previewDoc.title}</p>
                <p className="text-sm text-[#666666]">{previewDoc.original_name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Usage Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-800 mb-2">Com utilitzar els documents:</h4>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
            <li>Puja el document (PDF o imatge)</li>
            <li>Copia la URL fent clic a l'icona <Copy className="w-3 h-3 inline" /></li>
            <li>Enganxa la URL al contingut HTML: <code className="bg-blue-100 px-1 rounded">&lt;a href="URL" target="_blank"&gt;Text&lt;/a&gt;</code></li>
            <li>El document s'obrirà en una nova pestanya</li>
          </ol>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDocuments;
